import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { cn, formatNumber, formatPercent, timeAgo, formatDate, getStatusColor } from '@/lib/utils';
import { apiService as mockApi } from '@/api';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Tabs } from '@/components/ui/Tabs';
import type { Device, Job, Alert, PaginatedResponse } from '@/types';

type ReportTab = 'overview' | 'device_health' | 'patch_compliance' | 'alert_summary' | 'activity' | 'remote_sessions';

export function Reports() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<ReportTab>('overview');
  const [metrics, setMetrics] = useState<any>(null);
  const [devices, setDevices] = useState<Device[]>([]);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('30d');

  useEffect(() => {
    Promise.all([
      mockApi.getOverviewMetrics(),
      mockApi.getDevices({ pageSize: 500 } as any),
      mockApi.getJobs(),
      mockApi.getAlerts({}),
    ]).then(([m, d, j, a]) => {
      setMetrics(m);
      setDevices((d as PaginatedResponse<Device>).data);
      setJobs(j);
      setAlerts(a);
      setLoading(false);
    });
  }, []);

  const osDistribution = useMemo(() => {
    const map: Record<string, number> = {};
    for (const d of devices) {
      const os = d.os;
      map[os] = (map[os] || 0) + 1;
    }
    return Object.entries(map).sort((a, b) => b[1] - a[1]);
  }, [devices]);

  const topCpuDevices = useMemo(() => {
    return [...devices].sort((a, b) => b.cpu - a.cpu).slice(0, 10);
  }, [devices]);

  const topMemoryDevices = useMemo(() => {
    return [...devices].sort((a, b) => b.memory - a.memory).slice(0, 10);
  }, [devices]);

  const alertBySeverity = useMemo(() => {
    const map: Record<string, number> = { critical: 0, high: 0, medium: 0, low: 0, warning: 0 };
    for (const a of alerts) {
      map[a.severity] = (map[a.severity] || 0) + 1;
    }
    return map;
  }, [alerts]);

  const alertByState = useMemo(() => {
    const map: Record<string, number> = { open: 0, acknowledged: 0, resolved: 0, escalated: 0, assigned: 0 };
    for (const a of alerts) {
      map[a.state] = (map[a.state] || 0) + 1;
    }
    return map;
  }, [alerts]);

  const jobsByStatus = useMemo(() => {
    const map: Record<string, number> = {};
    for (const j of jobs) {
      map[j.status] = (map[j.status] || 0) + 1;
    }
    return map;
  }, [jobs]);

  const remoteSessions = useMemo(() => {
    return devices.flatMap(d => d.recentSessions || []);
  }, [devices]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-[var(--text-muted)] text-sm">Loading reports…</div>
      </div>
    );
  }

  const tabs: { id: ReportTab; label: string }[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'device_health', label: 'Device Health' },
    { id: 'patch_compliance', label: 'Patch Compliance' },
    { id: 'alert_summary', label: 'Alert Summary' },
    { id: 'activity', label: 'Activity' },
    { id: 'remote_sessions', label: 'Remote Sessions' },
  ];

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-[var(--text-primary)]">Reports</h1>
          <p className="text-sm text-[var(--text-muted)] mt-0.5">Last {dateRange === '30d' ? '30 days' : dateRange === '7d' ? '7 days' : '24 hours'}</p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="bg-[var(--panel)] border border-[var(--border)] rounded-md px-3 py-2 text-sm text-[var(--text-primary)]"
          >
            <option value="24h">Last 24 Hours</option>
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
          </select>
          <Button variant="outline" onClick={() => {
            const event = new CustomEvent('toast', { detail: { message: 'Report exported as CSV', type: 'success' } });
            window.dispatchEvent(event);
          }}>Export</Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-[var(--border)]">
        {tabs.map(t => (
          <button
            key={t.id}
            onClick={() => setActiveTab(t.id)}
            className={cn(
              'px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px',
              activeTab === t.id
                ? 'text-[var(--text-primary)] border-[var(--accent)]'
                : 'text-[var(--text-muted)] border-transparent hover:text-[var(--text-secondary)]'
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Overview */}
      {activeTab === 'overview' && metrics && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            <StatCard label="Total Devices" value={metrics.totalDevices} onClick={() => navigate('/devices')} />
            <StatCard label="Online" value={metrics.onlineDevices} color="var(--status-online)" onClick={() => navigate('/devices')} />
            <StatCard label="Offline" value={metrics.offlineDevices} color="var(--status-offline)" onClick={() => navigate('/devices')} />
            <StatCard label="Active Alerts" value={metrics.activeAlerts} color="var(--status-warning)" onClick={() => navigate('/alerts')} />
            <StatCard label="Running Jobs" value={metrics.runningJobs} onClick={() => navigate('/jobs')} />
            <StatCard label="Active Sessions" value={metrics.activeRemoteSessions} onClick={() => navigate('/remote-sessions')} />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="p-5">
              <h3 className="text-sm font-medium text-[var(--text-primary)] mb-4">Device Status Distribution</h3>
              <div className="space-y-3">
                {metrics.deviceStatusDistribution.map((s: any) => (
                  <div key={s.label} className="flex items-center gap-3">
                    <span className="text-xs text-[var(--text-muted)] w-20">{s.label}</span>
                    <div className="flex-1 h-6 bg-[var(--bg)] rounded overflow-hidden">
                      <div
                        className="h-full rounded transition-all"
                        style={{ width: `${(s.value / metrics.totalDevices) * 100}%`, background: s.color }}
                      />
                    </div>
                    <span className="text-sm text-[var(--text-primary)] tabular-nums w-10 text-right">{s.value}</span>
                  </div>
                ))}
              </div>
            </Card>

            <Card className="p-5">
              <h3 className="text-sm font-medium text-[var(--text-primary)] mb-4">System Averages</h3>
              <div className="space-y-4">
                <AvgBar label="CPU Usage" value={metrics.avgCpu} unit="%" />
                <AvgBar label="Memory Usage" value={metrics.avgMemory} unit="%" />
                <AvgBar label="Disk Usage" value={metrics.avgDisk} unit="%" />
                <div className="grid grid-cols-2 gap-4 pt-2">
                  <div className="bg-[var(--bg)] rounded-lg p-3">
                    <p className="text-xs text-[var(--text-muted)]">Patch Compliance</p>
                    <p className="text-2xl font-semibold text-[var(--text-primary)] tabular-nums mt-1">{metrics.patchCompliance}%</p>
                  </div>
                  <div className="bg-[var(--bg)] rounded-lg p-3">
                    <p className="text-xs text-[var(--text-muted)]">Antivirus Active</p>
                    <p className="text-2xl font-semibold text-[var(--text-primary)] tabular-nums mt-1">{metrics.antivirusActive}%</p>
                  </div>
                </div>
              </div>
            </Card>
          </div>

          {/* Alert Trend */}
          <Card className="p-5">
            <h3 className="text-sm font-medium text-[var(--text-primary)] mb-4">Alert Trend (14 Days)</h3>
            <div className="flex items-end gap-1 h-40">
              {metrics.alertTrend.map((day: any) => (
                <div key={day.date} className="flex-1 flex flex-col gap-0.5 group">
                  <div className="flex flex-col-reverse gap-0.5 h-full">
                    <div className="rounded-t bg-[var(--status-critical)] transition-all" style={{ height: `${(day.critical / 15) * 100}%` }} />
                    <div className="rounded-t bg-[var(--status-warning)] transition-all" style={{ height: `${(day.warning / 15) * 100}%` }} />
                    <div className="rounded-t bg-[var(--status-online)] transition-all" style={{ height: `${(day.resolved / 15) * 100}%` }} />
                  </div>
                  <span className="text-[10px] text-[var(--text-muted)] text-center mt-1 hidden md:block">{day.date.slice(5)}</span>
                </div>
              ))}
            </div>
            <div className="flex items-center gap-4 mt-3 text-xs text-[var(--text-muted)]">
              <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-[var(--status-critical)]" /> Critical</span>
              <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-[var(--status-warning)]" /> Warning</span>
              <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-[var(--status-online)]" /> Resolved</span>
            </div>
          </Card>
        </div>
      )}

      {/* Device Health */}
      {activeTab === 'device_health' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <AvgBarCard label="Avg CPU" value={metrics?.avgCpu || 0} />
            <AvgBarCard label="Avg Memory" value={metrics?.avgMemory || 0} />
            <AvgBarCard label="Avg Disk" value={metrics?.avgDisk || 0} />
          </div>

          <Card className="p-5">
            <h3 className="text-sm font-medium text-[var(--text-primary)] mb-4">Operating System Distribution</h3>
            <div className="space-y-2">
              {osDistribution.map(([os, count]) => (
                <div key={os} className="flex items-center gap-3">
                  <span className="text-sm text-[var(--text-secondary)] w-48 truncate">{os}</span>
                  <div className="flex-1 h-5 bg-[var(--bg)] rounded overflow-hidden">
                    <div className="h-full bg-[var(--accent)] rounded transition-all" style={{ width: `${(count / devices.length) * 100}%` }} />
                  </div>
                  <span className="text-sm text-[var(--text-primary)] tabular-nums w-12 text-right">{count}</span>
                </div>
              ))}
            </div>
          </Card>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="p-5">
              <h3 className="text-sm font-medium text-[var(--text-primary)] mb-4">Top 10 by CPU Usage</h3>
              <div className="space-y-2">
                {topCpuDevices.map((d) => (
                  <div key={d.id} className="flex items-center gap-3">
                    <span className="text-sm text-[var(--text-primary)] cursor-pointer hover:text-[var(--accent)] flex-1 truncate" onClick={() => navigate(`/devices/${d.id}`)}>{d.hostname}</span>
                    <div className="w-24 h-5 bg-[var(--bg)] rounded overflow-hidden">
                      <div className={cn('h-full rounded', d.cpu > 80 ? 'bg-[var(--status-critical)]' : d.cpu > 60 ? 'bg-[var(--status-warning)]' : 'bg-[var(--status-online)]')} style={{ width: `${d.cpu}%` }} />
                    </div>
                    <span className="text-sm tabular-nums w-10 text-right text-[var(--text-secondary)]">{d.cpu}%</span>
                  </div>
                ))}
              </div>
            </Card>
            <Card className="p-5">
              <h3 className="text-sm font-medium text-[var(--text-primary)] mb-4">Top 10 by Memory Usage</h3>
              <div className="space-y-2">
                {topMemoryDevices.map((d) => (
                  <div key={d.id} className="flex items-center gap-3">
                    <span className="text-sm text-[var(--text-primary)] cursor-pointer hover:text-[var(--accent)] flex-1 truncate" onClick={() => navigate(`/devices/${d.id}`)}>{d.hostname}</span>
                    <div className="w-24 h-5 bg-[var(--bg)] rounded overflow-hidden">
                      <div className={cn('h-full rounded', d.memory > 80 ? 'bg-[var(--status-critical)]' : d.memory > 60 ? 'bg-[var(--status-warning)]' : 'bg-[var(--status-online)]')} style={{ width: `${d.memory}%` }} />
                    </div>
                    <span className="text-sm tabular-nums w-10 text-right text-[var(--text-secondary)]">{d.memory}%</span>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </div>
      )}

      {/* Patch Compliance */}
      {activeTab === 'patch_compliance' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <StatCard label="Compliance Rate" value={`${metrics?.patchCompliance || 0}%`} color="var(--status-online)" />
            <StatCard label="Missing Updates" value={devices.filter(d => d.patchStatus === 'missing_updates').length} color="var(--status-warning)" />
            <StatCard label="Failed Updates" value={devices.filter(d => d.patchStatus === 'failed_updates').length} color="var(--status-critical)" />
            <StatCard label="Pending Reboots" value={metrics?.pendingReboots || 0} color="var(--status-warning)" />
          </div>
          <Card className="p-5">
            <h3 className="text-sm font-medium text-[var(--text-primary)] mb-4">Compliance by Device</h3>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {devices.map((d) => (
                <div key={d.id} className="flex items-center gap-3 py-1">
                  <span className="text-sm text-[var(--text-primary)] cursor-pointer hover:text-[var(--accent)] w-48 truncate" onClick={() => navigate(`/devices/${d.id}`)}>{d.hostname}</span>
                  <Badge variant={d.patchStatus === 'compliant' ? 'success' : d.patchStatus === 'missing_updates' ? 'warning' : 'error'}>
                    {d.patchStatus.replace('_', ' ')}
                  </Badge>
                  {d.pendingReboot && <Badge variant="warning">Reboot Pending</Badge>}
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}

      {/* Alert Summary */}
      {activeTab === 'alert_summary' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="p-5">
              <h3 className="text-sm font-medium text-[var(--text-primary)] mb-4">By Severity</h3>
              <div className="space-y-3">
                {Object.entries(alertBySeverity).map(([sev, count]) => (
                  <div key={sev} className="flex items-center gap-3">
                    <span className="text-sm text-[var(--text-muted)] w-20 capitalize">{sev}</span>
                    <div className="flex-1 h-5 bg-[var(--bg)] rounded overflow-hidden">
                      <div className="h-full rounded" style={{ width: `${(count / Math.max(alerts.length, 1)) * 100}%`, background: getStatusColor(sev) }} />
                    </div>
                    <span className="text-sm tabular-nums w-10 text-right text-[var(--text-primary)]">{count}</span>
                  </div>
                ))}
              </div>
            </Card>
            <Card className="p-5">
              <h3 className="text-sm font-medium text-[var(--text-primary)] mb-4">By State</h3>
              <div className="space-y-3">
                {Object.entries(alertByState).map(([state, count]) => (
                  <div key={state} className="flex items-center gap-3">
                    <span className="text-sm text-[var(--text-muted)] w-24 capitalize">{state}</span>
                    <div className="flex-1 h-5 bg-[var(--bg)] rounded overflow-hidden">
                      <div className="h-full rounded" style={{ width: `${(count / Math.max(alerts.length, 1)) * 100}%`, background: getStatusColor(state) }} />
                    </div>
                    <span className="text-sm tabular-nums w-10 text-right text-[var(--text-primary)]">{count}</span>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </div>
      )}

      {/* Activity */}
      {activeTab === 'activity' && (
        <div className="space-y-6">
          <Card className="p-5">
            <h3 className="text-sm font-medium text-[var(--text-primary)] mb-4">Jobs by Status</h3>
            <div className="grid grid-cols-3 md:grid-cols-6 gap-4">
              {Object.entries(jobsByStatus).map(([status, count]) => (
                <div key={status} className="bg-[var(--bg)] rounded-lg p-4 text-center">
                  <p className="text-2xl font-semibold tabular-nums" style={{ color: getStatusColor(status) }}>{count}</p>
                  <p className="text-xs text-[var(--text-muted)] capitalize mt-1">{status}</p>
                </div>
              ))}
            </div>
          </Card>
          <Card className="p-5">
            <h3 className="text-sm font-medium text-[var(--text-primary)] mb-4">Recent Jobs</h3>
            <div className="space-y-2">
              {jobs.slice(0, 15).map((j) => (
                <div key={j.id} className="flex items-center gap-3 py-2 border-b border-[var(--border-subtle)]">
                  <Badge variant={j.status === 'completed' ? 'success' : j.status === 'failed' ? 'error' : j.status === 'running' ? 'info' : 'default'}>{j.status}</Badge>
                  <span className="text-sm text-[var(--text-primary)] flex-1 truncate">{j.name}</span>
                  <span className="text-xs text-[var(--text-muted)]">{j.completedCount}/{j.targetCount}</span>
                  <span className="text-xs text-[var(--text-muted)]">{timeAgo(j.createdAt)}</span>
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}

      {/* Remote Sessions */}
      {activeTab === 'remote_sessions' && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard label="Total Sessions" value={remoteSessions.length} />
            <StatCard label="Active" value={remoteSessions.filter(s => s.status === 'active').length} color="var(--status-online)" />
            <StatCard label="Avg Duration" value={`${Math.round(remoteSessions.reduce((s, r) => s + r.duration, 0) / Math.max(remoteSessions.length, 1))}s`} />
            <StatCard label="Technicians" value={new Set(remoteSessions.map(s => s.technicianName)).size} />
          </div>
          <Card className="p-5">
            <h3 className="text-sm font-medium text-[var(--text-primary)] mb-4">Sessions by Technician</h3>
            <div className="space-y-2">
              {Object.entries(
                remoteSessions.reduce((acc, s) => {
                  acc[s.technicianName] = (acc[s.technicianName] || 0) + 1;
                  return acc;
                }, {} as Record<string, number>)
              ).sort((a, b) => b[1] - a[1]).map(([tech, count]) => (
                <div key={tech} className="flex items-center gap-3">
                  <span className="text-sm text-[var(--text-primary)] w-40">{tech}</span>
                  <div className="flex-1 h-5 bg-[var(--bg)] rounded overflow-hidden">
                    <div className="h-full bg-[var(--accent)] rounded" style={{ width: `${(count / remoteSessions.length) * 100}%` }} />
                  </div>
                  <span className="text-sm tabular-nums w-10 text-right">{count}</span>
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, color, onClick }: { label: string; value: number | string; color?: string; onClick?: () => void }) {
  return (
    <Card className={cn('p-4', onClick && 'cursor-pointer hover:border-[var(--border-strong)]')} onClick={onClick}>
      <p className="text-xs text-[var(--text-muted)]">{label}</p>
      <p className="text-2xl font-semibold mt-1 tabular-nums" style={{ color: color || 'var(--text-primary)' }}>{value}</p>
    </Card>
  );
}

function AvgBar({ label, value, unit }: { label: string; value: number; unit: string }) {
  const color = value > 80 ? 'var(--status-critical)' : value > 60 ? 'var(--status-warning)' : 'var(--status-online)';
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-sm text-[var(--text-secondary)]">{label}</span>
        <span className="text-sm font-medium text-[var(--text-primary)] tabular-nums">{value}{unit}</span>
      </div>
      <div className="h-2 bg-[var(--bg)] rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all" style={{ width: `${value}%`, background: color }} />
      </div>
    </div>
  );
}

function AvgBarCard({ label, value }: { label: string; value: number }) {
  const color = value > 80 ? 'var(--status-critical)' : value > 60 ? 'var(--status-warning)' : 'var(--status-online)';
  return (
    <Card className="p-5">
      <p className="text-xs text-[var(--text-muted)]">{label}</p>
      <p className="text-3xl font-semibold mt-2 tabular-nums" style={{ color }}>{value}%</p>
      <div className="h-2 bg-[var(--bg)] rounded-full overflow-hidden mt-3">
        <div className="h-full rounded-full transition-all" style={{ width: `${value}%`, background: color }} />
      </div>
    </Card>
  );
}
